/* eslint-disable complexity */

import React from 'react';
import PropTypes from 'prop-types';
import FrameworkUtils from 'utils/framework';
import BaseClass from 'components/BaseClass';

import { mediaQueries, screenReaderOnlyStyle } from 'style/config';
import {
    Box, Flex, Image, Text, Divider
} from 'components/ui';
import BreadCrumbs from 'components/Catalog/BreadCrumbs';
import CatalogLayout from 'components/Catalog/CatalogLayout';
import RegularFilters from 'components/Catalog/Filters';
import { withUpperFunnelProps } from 'viewModel/catalog/upperFunnel/withUpperFunnelProps';
import Categories from 'components/Catalog/Categories';
import RegularProductGrid from 'components/Catalog/ProductGrid';
import SeoText from 'components/Catalog/SeoText';
import SoftLinks from 'components/Catalog/SoftLinks';
import RelatedContent from 'components/RelatedContent';
import Copy from 'components/Content/Copy';
import ChanelBottomBanner from 'components/ChanelBottomBanner';
import MediaUtils from 'utils/Media';
import { PAGE_TYPES } from '@ufe/shared/constants/catalog';
import PixleeUtils from 'utils/pixlee';
import LazyLoad from 'components/LazyLoad';
import localeUtils from 'utils/LanguageLocale';
import catalogUtils from 'utils/Catalog';
import CatalogPageBindings from 'analytics/bindingMethods/pages/catalog/catalogPageBindings';
import PixleeContainer from 'components/Catalog/PixleeContainer/PixleeContainer';
import FAQCollection from 'components/Content/FAQCollection';
import { PostLoad } from 'constants/events';
import contentConstants from 'constants/content';
import deepExtend from 'utils/deepExtend';
import CatalogEN from 'components/Catalog/locales/Catalog_en_US';
import CatalogFR from 'components/Catalog/locales/Catalog_fr_CA';
import FreeShippingBanner from 'components/Banner/FreeShippingBanner';
import { fireRoktPageView } from 'utils/adUtils';
const getCatalogText = localeUtils.getLocalisedDataFromResource(CatalogEN, CatalogFR);

const Filters = withUpperFunnelProps(RegularFilters);
const ProductGrid = withUpperFunnelProps(RegularProductGrid);
const BRAND_ID_CHANEL = '1065';

const { FILTERS_SIDEBAR_WIDTH } = contentConstants;
const { wrapComponent } = FrameworkUtils;
const { loadPixlee } = PixleeUtils;
const { Media } = MediaUtils;

class NthBrandsPage extends BaseClass {
    constructor(props) {
        super(props);
        this.state = {
            prevContextId: null,
            selectedFilters: {},
            appliedFilters: {},
            showUpdateDefaultSort: props.showUpdateDefaultSort,
            initialFiltersSnapshot: null,
            trackFilterSelection: false
        };

        if (Sephora.Util?.InflatorComps?.services?.CatalogService) {
            Sephora.Util.InflatorComps.services.CatalogService.catalogEngine = props.catalog?.responseSource;
        }

        this.layoutWrapRef = React.createRef();
    }

    static getDerivedStateFromProps({ catalog, showUpdateDefaultSort }, state) {
        const pendingClientSideRender = catalog?.hasClientSideData && !state.hasClientSideData;

        if (catalog.contextId === state.prevContextId && !pendingClientSideRender) {
            return null;
        }

        const currentCategory = catalogUtils.getCategoryInfoFromCategories(catalog.categories, {
            parameter: 'targetUrl',
            value: catalog.seoCanonicalUrl
        });
        const pageId = currentCategory?.nodeValue || currentCategory?.nodeStr || catalog.seoCanonicalUrl;

        const isSamePage = pageId === state.prevPageId;
        const result = catalogUtils.createFiltersWithSortRefinements(catalog, state.selectedFilters, isSamePage, showUpdateDefaultSort);
        const fireAnalytics = state.prevContextId !== null && !pendingClientSideRender; // always true except hard load case

        if (fireAnalytics) {
            if (isSamePage) {
                CatalogPageBindings.fireAsyncPageLoadAnalytics(catalog, result.categoryFilters, state.trackFilterSelection); // on filters change
            } else {
                CatalogPageBindings.setPageLoadAnalytics(catalog, result.categoryFilters); // spa navigation analytics
            }
        }

        return {
            prevPageId: pageId,
            prevContextId: catalog.contextId,
            selectedFilters: result.filters,
            appliedFilters: result.filters,
            refinements: result.withSortRefinements,
            categoryFilters: result.categoryFilters,
            hasClientSideData: catalog?.hasClientSideData,
            trackFilterSelection: false
        };
    }

    componentWillUnmount() {
        this.unsubscribe();
        this.unsubscribeZipCode();
        window.removeEventListener(PostLoad, this.initPixlee);
    }

    componentDidMount() {
        const {
            catalog,
            onStoreChangedFromHeader = () => {},
            onZipCodeChangedFromHeader = () => {},
            isStoreIdAndZipCodeReady,
            validateUpperFunnelParams,
            getFulfillmentOptions = () => {}
        } = this.props;

        const { categoryFilters } = this.state;

        if (catalog?.categories) {
            CatalogPageBindings.setPageLoadAnalytics(catalog, categoryFilters); // hardload analytics
        }

        this.props.dispatchMarketingParams();
        this.unsubscribe = onStoreChangedFromHeader();
        this.unsubscribeZipCode = onZipCodeChangedFromHeader();

        if (this.isPixleeEnabled()) {
            window.addEventListener(PostLoad, this.initPixlee);
        }

        if (isStoreIdAndZipCodeReady && !catalog?.deliveryOptions) {
            validateUpperFunnelParams(getFulfillmentOptions);
        }

        const brandName = catalog?.displayName || '';
        fireRoktPageView({ pagetype: 'brand', screenname: brandName, title: brandName });
    }

    componentDidUpdate(prevProps) {
        const { catalog } = this.props;

        if (catalog?.seoCanonicalUrl && catalog.seoCanonicalUrl !== prevProps.catalog?.seoCanonicalUrl) {
            const brandName = catalog.displayName || '';
            fireRoktPageView({ pagetype: 'brand', screenname: brandName, title: brandName });
        }

        if (Sephora.Util?.InflatorComps?.services?.CatalogService) {
            Sephora.Util.InflatorComps.services.CatalogService.catalogEngine = catalog?.responseSource;
        }
    }

    shouldUseLiveUpdates = () => {
        return Sephora.configurationSettings.sortModalUpdates?.isEnabled;
    };

    onModalOpen = () => {
        if (this.shouldUseLiveUpdates()) {
            this.setState({
                initialFiltersSnapshot: deepExtend({}, this.state.appliedFilters)
            });
        }
    };

    confirmSelection = () => {
        if (this.shouldUseLiveUpdates()) {
            this.setState({ initialFiltersSnapshot: null });
        }
    };

    selectFilters = (filtersToSelect, applyFilters = false) => {
        const newSelectedFilters = catalogUtils.addToSelection(this.state.selectedFilters, filtersToSelect);
        const shouldApplyFilters = applyFilters || this.shouldUseLiveUpdates();
        const sortSelected = filtersToSelect[getCatalogText('sort')]?.length > 0;
        const hasNewFilters = catalogUtils.hasNewFiltersAdded(this.state.selectedFilters, filtersToSelect);
        const trackFilterSelection = shouldApplyFilters && !sortSelected && hasNewFilters;
        this.applySelectionIfChanged(newSelectedFilters, shouldApplyFilters, trackFilterSelection);
    };

    removeFilterValue = (filterKey, filterValue, applyFilters = false) => {
        const newSelectedFilters = catalogUtils.removeValueFromSelection(this.state.selectedFilters, filterKey, filterValue);
        this.applySelectionIfChanged(newSelectedFilters, applyFilters || this.shouldUseLiveUpdates());
    };

    clearFiltersSelection = (applyFilters, resetSortToDefault) => {
        const { selectedFilters } = this.state;
        const newSelectedFilters = catalogUtils.resetSelection(selectedFilters, resetSortToDefault);
        this.applySelectionIfChanged(newSelectedFilters, applyFilters || this.shouldUseLiveUpdates());
    };

    discardSelection = (applyFilters = false) => {
        const { initialFiltersSnapshot } = this.state;

        if (this.shouldUseLiveUpdates() && initialFiltersSnapshot) {
            this.applySelectionIfChanged({ ...initialFiltersSnapshot }, true);
            this.setState({ initialFiltersSnapshot: null });
        } else {
            this.applySelectionIfChanged(this.state.appliedFilters, applyFilters);
        }
    };

    applySelectionIfChanged = (newSelectedFilters, applyFilters = false, trackFilterSelection = false) => {
        const { selectedFilters: currentSelectedFilters } = this.state;

        if (JSON.stringify(newSelectedFilters) !== JSON.stringify(currentSelectedFilters)) {
            const nextState = { selectedFilters: newSelectedFilters, trackFilterSelection };

            if (applyFilters && this.shouldUseLiveUpdates()) {
                nextState.appliedFilters = { ...newSelectedFilters };
            }

            this.setState(nextState, () => {
                if (applyFilters) {
                    this.applySelectedFilters();
                }
            });
        }
    };

    applySelectedFilters = (trackFilterSelection = false) => {
        if (trackFilterSelection) {
            this.setState({ trackFilterSelection: true });
        }

        const { selectedFilters, refinements } = this.state;
        const filtersToApply = catalogUtils.createFiltersToApply(selectedFilters, refinements);
        this.props.applyFilters(filtersToApply);
    };

    getCurrentPageFromCatalog = ({ currentPage }) => {
        return currentPage || 1;
    };

    isPixleeEnabled = () => {
        const { catalog } = this.props;

        // remove `brandUGCWidget` after successful contentful A/B test - ECOM-1601
        return Sephora.configurationSettings.enableUGCWidget && (catalog.content?.ugcWidget?.widgetId || catalog.brandUGCWidget);
    };

    initPixlee = () => {
        loadPixlee();
    };

    getPixleeWidgetId = () => {
        if (this.isPixleeEnabled() && !this.props.isAnonymous) {
            const { catalog } = this.props;
            // remove `locale` + `brandUGCWidget` after successful contentful A/B test - ECOM-1602
            const locale = `${localeUtils.getCurrentCountry().toLowerCase()}-${localeUtils.getCurrentLanguage().toLowerCase()}`;

            return catalog.content?.ugcWidget?.widgetId || catalog.brandUGCWidget?.[locale];
        } else {
            return null;
        }
    };

    render() {
        const { catalog, persistFiltersOnBrand, showSimplifyFiltersOnPLP } = this.props;
        const {
            categories,
            brandId,
            contextId,
            linkEquityBlock = {},
            quickFilter,
            pageType,
            displayName,
            targetUrl,
            seoCanonicalUrl,
            schemas,
            resultId,
            content
        } = catalog;
        const { selectedFilters, appliedFilters, refinements } = this.state;

        const seoContent = catalog.seoMetaContent || catalog.seoContent || [];
        const hasSeoContent = seoContent.length > 0 || catalog?.seoText;
        const currentCategory = catalogUtils.getCategoryInfoFromCategories(categories, {
            parameter: 'targetUrl',
            value: seoCanonicalUrl
        });
        const pageId = currentCategory?.nodeValue || currentCategory?.nodeStr || seoCanonicalUrl;

        const categoriesProps = {
            contextId,
            pageType,
            categories,
            categoryId: currentCategory?.nodeValue || currentCategory?.nodeStr,
            shouldPersistFilters: persistFiltersOnBrand
        };

        const filters = (
            <Filters
                pageId={pageId}
                contextId={contextId}
                selectedFilters={selectedFilters}
                appliedFilters={appliedFilters}
                refinements={refinements}
                selectFilters={this.selectFilters}
                discardSelection={this.discardSelection}
                clearFiltersSelection={this.clearFiltersSelection}
                applySelectedFilters={this.applySelectedFilters}
                removeFilterValue={this.removeFilterValue}
                onModalOpen={this.onModalOpen}
                confirmSelection={this.confirmSelection}
                shouldStickySideBar
                targetShadowScrollY={182}
                totalProducts={catalog?.totalProducts}
                showCompactPills
                showSimplifyFiltersOnPLP={showSimplifyFiltersOnPLP}
            />
        );

        const showBreadcrumb = pageType !== PAGE_TYPES.TOP_CATEGORY;

        const breadcrumbProps = {
            key: contextId,
            categories,
            categoryId: currentCategory?.nodeValue || currentCategory?.nodeStr,
            pageType: 'brand',
            brand: {
                displayName,
                targetUrl
            }
        };

        const isChanel = brandId === BRAND_ID_CHANEL;
        const pixleeWidgetId = this.getPixleeWidgetId();

        return (
            <div ref={this.layoutWrapRef}>
                {this.props.isOptimizeShippingCopyPLPActive && this.props.isAnonymous && (
                    <Media lessThan='md'>
                        <FreeShippingBanner />
                    </Media>
                )}
                <CatalogLayout
                    content={{
                        top: showBreadcrumb && <BreadCrumbs {...breadcrumbProps} />,
                        sidebar: (
                            <CatalogLayout.SideBar shouldStickySideBar>
                                <Text
                                    is='div'
                                    fontSize={['lg', 'xl']}
                                    lineHeight='tight'
                                    marginBottom={[null, null, '1em']}
                                    fontWeight='bold'
                                    css={[{ order: -1 }, isChanel && { [mediaQueries.smMax]: screenReaderOnlyStyle }]}
                                    children={catalog.displayName}
                                />
                                {pageType === PAGE_TYPES.NTH_CATEGORY_PAGE ? (
                                    <>
                                        {filters}
                                        <Media greaterThan='sm'>
                                            <Categories
                                                {...categoriesProps}
                                                showHeading={true}
                                            />
                                        </Media>
                                    </>
                                ) : (
                                    <>
                                        <Categories {...categoriesProps} />
                                        {filters}
                                    </>
                                )}
                            </CatalogLayout.SideBar>
                        ),
                        main: (
                            <>
                                {this.props.isOptimizeShippingCopyPLPActive && this.props.isAnonymous && (
                                    <Media greaterThan='sm'>
                                        <FreeShippingBanner />
                                    </Media>
                                )}
                                {isChanel && (
                                    <Flex
                                        alignItems='center'
                                        marginX={['-container', 0]}
                                        paddingX={['container', 5]}
                                        marginBottom={[null, 3]}
                                        borderTop={'6px solid black'}
                                        justifyContent='center'
                                        height={[42, 82]}
                                        order={[-1, 0]}
                                    >
                                        <Image
                                            display='block'
                                            disableLazyLoad={true}
                                            src='/img/ufe/logo-chanel.svg'
                                            width={[74, 127]}
                                            height={[12, 20]}
                                        />
                                    </Flex>
                                )}
                                <ProductGrid
                                    h1={catalog.seoTitle ? catalog.seoTitle.replace(' | Sephora', '') : catalog.displayName}
                                    isBrand={true}
                                    source={'brand'}
                                    layoutWrapRef={this.layoutWrapRef}
                                    selectedFilters={selectedFilters}
                                    appliedFilters={appliedFilters}
                                    refinements={refinements}
                                    selectFilters={this.selectFilters}
                                    removeFilterValue={this.removeFilterValue}
                                    clearAllFilters={this.clearFiltersSelection}
                                    isBrowse
                                    addItemCnstrcData
                                    showSimplifyFiltersOnPLP={showSimplifyFiltersOnPLP}
                                />
                                {isChanel && <ChanelBottomBanner marginTop={[null, 5]} />}
                            </>
                        ),
                        bottom: (
                            <div key={contextId}>
                                <SoftLinks
                                    key={contextId}
                                    isBottom={true}
                                    links={content?.softLinks || quickFilter}
                                />
                                {(content?.faqHeader || content?.faqCollection?.items?.length) && (
                                    <FAQCollection
                                        faqHeader={content.faqHeader}
                                        faqCollection={content.faqCollection}
                                    />
                                )}
                                {showBreadcrumb && (
                                    <BreadCrumbs
                                        {...breadcrumbProps}
                                        isBottom={true}
                                    />
                                )}
                                {pixleeWidgetId && (
                                    <LazyLoad
                                        component={PixleeContainer}
                                        categoryId={brandId}
                                        widgetId={pixleeWidgetId}
                                        containerId='PixleeContainer'
                                    />
                                )}
                                <Categories
                                    {...categoriesProps}
                                    title={catalog.displayName}
                                    isBottom={true}
                                />
                                {content?.copy?.length > 0 && content?.copy?.[0]?.content && (
                                    <>
                                        <Divider
                                            marginTop={[4, 5]}
                                            marginBottom={0}
                                        />
                                        {content?.copy?.map((item, index) => (
                                            <Copy
                                                content={item.content}
                                                key={item.sid}
                                                marginTop={[4, index === 0 ? 5 : 4]}
                                                marginBottom={index === content.copy.length - 1 && hasSeoContent ? [4, 5] : 0}
                                            />
                                        ))}
                                    </>
                                )}
                                {hasSeoContent && (
                                    <Box>
                                        <Divider
                                            marginTop={[4, 5]}
                                            marginBottom={[4, 5]}
                                        />
                                        <SeoText
                                            contextId={pageId}
                                            content={seoContent}
                                            text={catalog?.seoText}
                                        />
                                    </Box>
                                )}
                                <RelatedContent
                                    hasDivider={hasSeoContent || content?.copy?.length > 0 || categoriesProps?.categories?.length > 0}
                                    links={linkEquityBlock.links}
                                />
                            </div>
                        )
                    }}
                    currentPage={this.getCurrentPageFromCatalog(catalog)}
                    isBrand={true}
                    columnWidth={FILTERS_SIDEBAR_WIDTH.STICKY}
                    backToTopPosition={110}
                />
                {schemas?.length &&
                    schemas.map(schema => (
                        <script
                            key={schema['@type']}
                            type='application/ld+json'
                            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
                        ></script>
                    ))}
                {resultId && (
                    <script
                        id='cnstrc-data'
                        type='application/json'
                        dangerouslySetInnerHTML={{ __html: JSON.stringify({ resultId }) }}
                    ></script>
                )}
            </div>
        );
    }
}

NthBrandsPage.propTypes = {
    getFulfillmentOptions: PropTypes.func,
    onStoreChangedFromHeader: PropTypes.func,
    onZipCodeChangedFromHeader: PropTypes.func
};

export default wrapComponent(NthBrandsPage, 'NthBrandsPage', true);
